import React from 'react';

import { Chip } from '@mui/material';
import _ from 'es-toolkit/compat';

import { VerificationRequestStatusLabels } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import { DataDisputeItemStatus, VerificationRequestStatus } from 'src/api';
import { DATA_DISPUTE_STATUS_LABEL } from 'src/constants/dataDispute';
import { SxProp } from 'src/types/mui';

interface InboxItemStatusChipProps {
  status: Nullable<string>;
  isVerificationRequest: boolean;
  isApiVerificationRequest?: boolean;
}
export const InboxItemStatusChip: React.FC<InboxItemStatusChipProps> = ({
  status,
  isVerificationRequest,
  isApiVerificationRequest = false,
}) => {
  if (isApiVerificationRequest) {
    return (
      <Chip sx={apiVerificationRequestChipStyles} label={status} size="small" variant="filled" />
    );
  }

  if (isVerificationRequest) {
    return (
      <Chip
        sx={_.get(
          verificationRequestChipStyles,
          status || VerificationRequestStatus.EmployeeToRespond,
        )}
        label={_.get(
          VerificationRequestStatusLabels,
          status || VerificationRequestStatus.EmployeeToRespond,
        )}
        size="small"
        variant="filled"
      />
    );
  }

  return (
    <Chip
      sx={_.get(dataDisputeChipStyles, status || DataDisputeItemStatus.InProgress)}
      label={_.get(DATA_DISPUTE_STATUS_LABEL, status || DataDisputeItemStatus.InProgress)}
      size="small"
      variant="outlined"
    />
  );
};

const apiVerificationRequestChipStyles: SxProp = theme => ({
  backgroundColor: theme.palette.chip?.success?.main,
  color: theme.palette.chip?.success?.contrastText,
  borderColor: theme.palette.chip?.success?.contrastText,
});

const verificationRequestChipStyles: Record<VerificationRequestStatus, SxProp> = {
  [VerificationRequestStatus.EmployeeToRespond]: theme => ({
    backgroundColor: theme.palette.chip?.warning?.main,
    color: theme.palette.chip?.warning?.contrastText,
    borderColor: theme.palette.chip?.warning?.contrastText,
  }),
  [VerificationRequestStatus.DeclinedByEmployee]: theme => ({
    backgroundColor: theme.palette.chip?.error?.main,
    color: theme.palette.chip?.error?.contrastText,
    borderColor: theme.palette.chip?.error?.contrastText,
  }),
  [VerificationRequestStatus.ReportCreated]: theme => ({
    backgroundColor: theme.palette.chip?.info?.main,
    color: theme.palette.chip?.info?.contrastText,
    borderColor: theme.palette.chip?.info?.contrastText,
  }),
  [VerificationRequestStatus.ReviewedByVerifier]: theme => ({
    backgroundColor: theme.palette.chip?.success?.main,
    color: theme.palette.chip?.success?.contrastText,
    borderColor: theme.palette.chip?.success?.contrastText,
  }),
  [VerificationRequestStatus.VerifierToRespond]: theme => ({
    backgroundColor: theme.palette.chip?.warning?.main,
    color: theme.palette.chip?.warning?.contrastText,
    borderColor: theme.palette.chip?.warning?.contrastText,
  }),
  [VerificationRequestStatus.DeclinedByVerifier]: theme => ({
    backgroundColor: theme.palette.chip?.error?.main,
    color: theme.palette.chip?.error?.contrastText,
    borderColor: theme.palette.chip?.error?.contrastText,
  }),
};

const dataDisputeChipStyles: Record<DataDisputeItemStatus, SxProp> = {
  [DataDisputeItemStatus.InProgress]: theme => ({
    backgroundColor: theme.palette.chip?.warning?.main,
    color: theme.palette.chip?.warning?.contrastText,
    borderColor: theme.palette.chip?.warning?.contrastText,
  }),
  [DataDisputeItemStatus.Resolved]: theme => ({
    backgroundColor: theme.palette.chip?.success?.main,
    color: theme.palette.chip?.success?.contrastText,
    borderColor: theme.palette.chip?.success?.contrastText,
  }),
};
